import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, Badge, ErrorAlert, Icon, PageHeader, Spinner, usePolling, useAsyncAction } from '@pryme/ui';
import { EventList } from '../components/EventList';
import { useDev } from '../lib/context';
import { isFailure } from '../lib/events';
import { activeKeys, canDevelop, STATUS_LABEL, statusTone, verifiedOrgDomains, type DevEvent, type Service } from '../lib/types';

type TaggedEvent = DevEvent & { serviceId: string };

function nextStep(s: Service): { label: string; to: string } | null {
  if (s.status === 'pending_credentials') {
    if (s.redirectUris.length === 0) return { label: 'Add redirect URIs', to: `/services/${s.id}/redirects` };
    return { label: 'Generate credentials', to: `/services/${s.id}/credentials` };
  }
  if (s.status === 'pending_activation') {
    if (s.environment === 'production' && !s.domain) return { label: 'Verify domain', to: '/domains' };
    return { label: 'Activate', to: `/services/${s.id}` };
  }
  return null;
}

const envName = (e: string) => (e === 'production' ? 'Production' : 'Sandbox');

function CreateOrgCard() {
  const { reloadOrgs, selectOrg, environment } = useDev();
  const [name, setName] = useState('');
  const create = useAsyncAction();
  return (
    <div style={{ maxWidth: 640 }}>
      <PageHeader
        eyebrow={`Get started · ${envName(environment)}`}
        title="Create your organization"
        lead="Services, credentials and team membership belong to an organization. You become its owner and can invite teammates later."
      />
      <section className="p-card">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await create.run(() => api<{ id: string }>('/v1/orgs', { body: { displayName: name.trim() } }));
            if (r) {
              await reloadOrgs();
              selectOrg(r.id);
            }
          }}
        >
          <div className="p-field">
            <label htmlFor="org-name">Organization name</label>
            <input id="org-name" className="p-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required placeholder="Acme Shop ApS" />
            <span className="p-hint">Usually your company’s legal name. Organizations are separate per environment: this one exists in {envName(environment)} only.</span>
          </div>
          <ErrorAlert error={create.error} />
          <button className="p-btn" disabled={create.busy || !name.trim()}>{create.busy ? <Spinner label="Creating…" /> : 'Create organization'}</button>
        </form>
      </section>
      <p className="p-small p-muted">
        Next: register a service, add redirect URIs, issue credentials and activate. The <Link to="/docs">quickstart</Link> walks through each step.
      </p>
    </div>
  );
}

export function Dashboard() {
  const { org, orgs, services, servicesLoading, servicesError, reloadServices, environment, orgDomains } = useDev();
  const production = environment === 'production';
  const [events, setEvents] = useState<TaggedEvent[]>([]);

  const loadEvents = useCallback(async () => {
    const lists = await Promise.all(
      services.slice(0, 12).map((s) =>
        api<{ events: DevEvent[] }>(`/v1/services/${s.id}/events`).then((r) => r.events.slice(0, 25).map((e) => ({ ...e, serviceId: s.id }))).catch(() => []),
      ),
    );
    setEvents(lists.flat().sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)).slice(0, 30));
  }, [services]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);
  usePolling(() => void loadEvents(), 15000, services.length > 0);

  if (orgs.length === 0 && !org) return <CreateOrgCard />;

  const totals = {
    services: services.length,
    active: services.filter((s) => s.status === 'active').length,
    domains: verifiedOrgDomains(orgDomains).length,
    keys: services.reduce((n, s) => n + activeKeys(s).length, 0),
    connections: services.reduce((n, s) => n + s.connections, 0),
  };
  const failures = events.filter(isFailure);
  const nameOf = (e: DevEvent) => services.find((s) => s.id === (e as TaggedEvent).serviceId)?.displayName;
  const stats: [string, number][] = [
    ['Services', totals.services],
    ['Active services', totals.active],
    ...(production ? [['Verified domains', totals.domains] as [string, number]] : []),
    ['Active keys', totals.keys],
    ['Connected people', totals.connections],
  ];

  return (
    <>
      <PageHeader
        eyebrow={`Organization · ${envName(environment)}`}
        title={org?.display_name ?? 'Dashboard'}
        lead={`Services, keys and recent verification activity${production ? ', and the domains your organization has verified' : ''}.`}
        actions={canDevelop(org?.role) && <Link className="p-btn" to="/services/new"><Icon name="plus" size={15} />Register a service</Link>}
      />

      <div className="p-stats" role="list" aria-label="Summary">
        {stats.map(([label, n]) => (
          <div key={label} role="listitem">
            <span className="p-stat">{n}</span>
            <span className="p-stat-label">{label}</span>
          </div>
        ))}
      </div>

      <ErrorAlert error={servicesError} />
      <section className="p-card flush" aria-labelledby="svc-h">
        <div className="p-card-head">
          <h2 id="svc-h">Services</h2>
          <button type="button" className="p-btn ghost small" onClick={() => void reloadServices()} disabled={servicesLoading}>
            {servicesLoading ? <Spinner /> : 'Refresh'}
          </button>
        </div>
        {services.length === 0 ? (
          <div className="p-card-body">
            <div className="p-empty" style={{ margin: 0 }}>
              <strong>No services yet</strong>
              <p>A service is a website or app that signs people in with Pryme or accepts requests from their agents. Register one to receive credentials.</p>
              {canDevelop(org?.role) && <Link className="p-btn" to="/services/new">Register your first service</Link>}
            </div>
          </div>
        ) : (
          <div className="p-table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="p-table">
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col">Status</th>
                  {production && <th scope="col">Domain</th>}
                  <th scope="col">Active keys</th>
                  <th scope="col">Connections</th>
                  <th scope="col"><span className="p-skip">Next step</span></th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => {
                  const step = nextStep(s);
                  return (
                    <tr key={s.id}>
                      <td>
                        <Link to={`/services/${s.id}`}><strong style={{ color: 'inherit' }}>{s.displayName}</strong></Link>
                        <div className="p-small p-muted mono">{s.id}</div>
                      </td>
                      <td><span className={`p-status ${statusTone(s.status)}`}>{STATUS_LABEL[s.status]}</span></td>
                      {production && <td>{s.domain ? <code>{s.domain}</code> : <span className="p-muted">—</span>}</td>}
                      <td>
                        {activeKeys(s, 'A').length} A · {activeKeys(s, 'B').length} B
                      </td>
                      <td>{s.connections}</td>
                      <td style={{ textAlign: 'right' }}>{step && <Link className="p-btn secondary small" to={step.to}>{step.label}</Link>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="p-grid">
        <section className="p-card" aria-labelledby="err-h">
          <div className="p-row p-between" style={{ marginBottom: '0.75rem' }}>
            <h2 id="err-h" style={{ margin: 0 }}>Recent errors</h2>
            {failures.length > 0 && <Badge tone="bad">{failures.length}</Badge>}
          </div>
          {failures.length ? (
            <EventList events={failures.slice(0, 8)} compact serviceName={nameOf} />
          ) : (
            <p className="p-muted p-small" style={{ margin: 0 }}>No failed signatures, replays or rejections recently.</p>
          )}
        </section>
        <section className="p-card" aria-labelledby="act-h">
          <h2 id="act-h">Recent activity</h2>
          <EventList events={events.slice(0, 8)} compact serviceName={nameOf} />
        </section>
      </div>
    </>
  );
}
